import React, { useState, useEffect } from 'react';

import { Alert, InputNumber } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { getContactName, getCustomer } from '@hooks/API/Contact/UseContactsList';
import { forceReloadTransaction } from '@hooks/API/UseTransaction';
import useConfigurations from '@hooks/Configurations/UseConfigurations';
import useTransactionLockedStatus from '@hooks/Utils/UseTransactionLockedStatus';

import CustomSwitch from '@components/Gui/CustomSwitch';
import { NwCancelButton, NwSaveButton, NwButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import ContactWithBillingInfo from '@components/Gui/NwForm/ContactWithBillingInfo';
import { NwFakeForm, NwFakeFormItem } from '@components/Gui/NwForm/NwFakeFormWrapper';
import { NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';
import NwLink from '@components/Gui/NwLink';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    .suggestions-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        h2 {
            margin-bottom: 0.5rem;
            font-size: ${(props) => props.theme.FontSizeMedium};
        }
        padding-bottom: 2rem;
    }
`;

const TransactionAddMotAgCommission = ({ transaction, accountingUnlock }) => {
    const lock = useTransactionLockedStatus(transaction);
    const canEdit = !lock.isLocked || (lock.isAccountant && accountingUnlock);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [agencySuggestions, setAgencySuggestions] = useState([]);

    const configuration = useConfigurations();
    const configurationCommissionOnGross = configuration.defaultMotherAgencyCommissionPercOnGross;
    const configurationCommission = configuration.defaultMotherAgencyCommissionPerc;

    const [selectedAgencySuggestion, setSelectedAgencySuggestion] = useState(null);

    const [agencyID, setAgencyID] = useState(null);
    const [agencyBillingInfoID, setAgencyBillingInfoID] = useState(null);
    const [agencyBillingInfos, setAgencyBillingInfos] = useState([]);
    const [percentage, setPercentage] = useState(configurationCommission);
    const [onGross, setOnGross] = useState(configurationCommissionOnGross);

    const [error, setError] = useState(null);

    useEffect(() => {
        if (drawerOpen) {
            setError(null);
            setAgencyID(null);
            setAgencyBillingInfoID(null);
            setAgencyBillingInfos([]);
            setPercentage(configurationCommission);
            setOnGross(configurationCommissionOnGross);
            setSelectedAgencySuggestion(null);
        }
    }, [drawerOpen]);

    const saveAgencyCommission = async () => {
        const validation = validateForm();
        if (validation) {
            return;
        }
        const dataToSend = {
            TransactionID: transaction.ID,
            Percentage: percentage,
            OnGross: onGross,
            AgencyID: agencyID,
            AgencyBillingInfoID: agencyBillingInfoID,
        };

        const response = await Axios.post(`/transactions/jobfee/addMotherAgencyCommission`, dataToSend);
        forceReloadTransaction(transaction.ID);
        setDrawerOpen(false);
    };

    const addMotherAgencyCommission = async () => {
        const modelAgencies = await Axios.get(`/models/getAgencies/${transaction.ModelID}`);
        const modelAgenciesWithPercentage = modelAgencies.filter((agency) => agency.Percentage > 0);
        const modelAgenciesWithPercentageNotInTransaction = modelAgenciesWithPercentage.filter(
            (agency) => !transaction.AgencyStatements.some((statement) => statement.ContactID === agency.ContactID)
        );
        const suggestions = modelAgenciesWithPercentageNotInTransaction.map((agency) => ({
            ID: agency.AgencyID,
            Name: getContactName(agency.AgencyID),
            Type: agency.TypeName,
            Percentage: agency.Percentage,
            PercentageOnGross: agency.OnGross,
        }));
        setAgencySuggestions(suggestions);
        setDrawerOpen(true);
    };

    const getAgencyDefaultCommission = (agencyID) => {
        const c = getCustomer(agencyID);
        if (c && c.DC && c.DC.length > 0) {
            return {
                Percentage: c.DC[0].MAC || configurationCommission,
                OnGross: c.DC[0].MACOG !== null ? c.DC[0].MACOG : configurationCommissionOnGross,
            };
        }
        return {
            Percentage: configurationCommission,
            OnGross: configurationCommissionOnGross,
        };
    };

    const updatePercentageAndOnGross = (value, setFieldValue) => {
        const defaultCommission = getAgencyDefaultCommission(value.ContactID);
        setPercentage(defaultCommission.Percentage);
        setOnGross(defaultCommission.OnGross);
    };

    const onSuggestionSelected = async (suggestion) => {
        setSelectedAgencySuggestion(suggestion);
        setPercentage(suggestion.Percentage);
        setOnGross(suggestion.PercentageOnGross);
        const contactsBillingInfos = await Axios.get(`/contacts/billinginfos/${suggestion.ID}`);
        setAgencyBillingInfos(contactsBillingInfos);
        setAgencyBillingInfoID(contactsBillingInfos[0].ID);
        setAgencyID(suggestion.ID);
    };

    const removeSelectedAgencySuggestion = () => {
        setSelectedAgencySuggestion(null);
        setAgencyID(null);
        setAgencyBillingInfoID(null);
        setAgencyBillingInfos([]);
        setPercentage(configurationCommission);
        setOnGross(configurationCommissionOnGross);
    };

    const validateForm = () => {
        let hasErrors = false;
        if (!agencyID) {
            setError('Please select an agency');
            hasErrors = true;
        }
        if (!percentage) {
            setError('Please input a percentage');
            hasErrors = true;
        } else {
            const validation = validateCommission(percentage);
            if (validation) {
                setError(validation);
                hasErrors = true;
            }
        }
        if (!hasErrors) {
            setError(null);
        }
        return hasErrors;
    };

    const validateCommission = (value) => {
        if (!/^\s*(?=.*[1-9])\d*(?:\.\d{1,2})?\s*$/.test(String(value))) {
            return 'Please input only two decimals';
        }
        if (parseFloat(value) < 0 || parseFloat(value) > 99.99) {
            return 'Please input correct percentage, it should be between 0 and 99.99';
        }
    };

    useEffect(() => {
        if (drawerOpen && error) {
            validateForm();
        }
    }, [drawerOpen, agencyID, agencyBillingInfoID, percentage, onGross, error]);

    return (
        <>
            <p>
                <NwLink
                    icon={byPrefixAndName.fass['plus-circle']}
                    disabled={!canEdit}
                    onClick={addMotherAgencyCommission}
                >
                    Add Mother Agency Commission
                </NwLink>
            </p>
            {drawerOpen && (
                <NwDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Add Mot. Ag. Commission">
                    <Wrapper>
                        {agencySuggestions.length > 0 && (
                            <div className="suggestions-container">
                                <h2>{getContactName(transaction.ModelID)}'s agencies</h2>
                                {agencySuggestions.map((suggestion, index) => (
                                    <NwLink
                                        icon={byPrefixAndName.fass['plus-circle']}
                                        key={suggestion.ID}
                                        onClick={() => onSuggestionSelected(suggestion)}
                                    >
                                        {suggestion.Name} ({suggestion.Percentage}%
                                        {suggestion.PercentageOnGross ? ' on gross' : ' on net'})
                                    </NwLink>
                                ))}
                            </div>
                        )}
                        <NwFakeForm vertical>
                            {selectedAgencySuggestion ? (
                                <div className="suggestion-selected">
                                    <NwFakeFormItem
                                        label="Agency"
                                        control={
                                            <p>
                                                <strong>{selectedAgencySuggestion.Name}</strong>
                                                <NwButton
                                                    iconOnly
                                                    ghost
                                                    danger
                                                    icon={byPrefixAndName.fass['times-circle']}
                                                    onClick={removeSelectedAgencySuggestion}
                                                    style={{ marginLeft: '0.25rem' }}
                                                />
                                            </p>
                                        }
                                    />
                                </div>
                            ) : (
                                <NwFakeFormItem
                                    label="Agency"
                                    control={
                                        <ContactWithBillingInfo
                                            fullSelector
                                            value={{ ContactID: agencyID, ContactBillingInfoID: agencyBillingInfoID }}
                                            billingInfos={agencyBillingInfos.length > 0 ? agencyBillingInfos : null}
                                            activeTypes={['age']}
                                            label="Agency"
                                            name="AgencyID"
                                            required
                                            onChange={(value) => {
                                                if (!value) {
                                                    setAgencyID(null);
                                                    setAgencyBillingInfoID(null);
                                                } else {
                                                    setAgencyID(value.ContactID);
                                                    setAgencyBillingInfoID(value.ContactBillingInfoID);
                                                }
                                                updatePercentageAndOnGross(value);
                                            }}
                                        />
                                    }
                                />
                            )}

                            <NwFakeFormItem
                                label="Percentage"
                                control={
                                    <InputNumber
                                        value={percentage}
                                        onChange={(value) => {
                                            setPercentage(value);
                                        }}
                                    />
                                }
                            />

                            <NwFakeFormItem
                                label="On Gross"
                                control={
                                    <CustomSwitch
                                        checked={Boolean(onGross)}
                                        onChange={(value) => {
                                            setOnGross(value);
                                        }}
                                    />
                                }
                            />
                            <br />
                            {error && <Alert message={error} type="error" showIcon />}
                            <br />
                            <NwFormButtonsBar
                                left={
                                    <NwCancelButton
                                        onClick={() => {
                                            setDrawerOpen(false);
                                        }}
                                    />
                                }
                                right={<NwSaveButton disabled={error} onClick={saveAgencyCommission} />}
                            />
                        </NwFakeForm>
                    </Wrapper>
                </NwDrawer>
            )}
        </>
    );
};

export default TransactionAddMotAgCommission;
